import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as Icons from 'lucide-react';
import { Reveal } from '../Reveal';
import { coach } from '../../config/coach';

const ApproachSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);
  const { kicker, heading, pillars } = coach.approach;

  return (
    <section id="approach" className="relative bg-black" data-testid="approach-section">
      {/* Parallax visual band — Website3 */}
      <div ref={ref} className="relative h-[58vh] sm:h-[72vh] overflow-hidden">
        <motion.div className="absolute inset-0 -top-[12%] -bottom-[12%] will-change-transform" style={{ y }}>
          <img
            src={coach.images.approach}
            alt={`${coach.fullName} training with purpose`}
            className="w-full h-full object-cover object-[50%_35%] img-grade"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black" />

        <div className="relative z-10 h-full mx-auto max-w-7xl px-5 sm:px-8 flex items-end pb-12 sm:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="kicker">{kicker}</span>
            <h2 className="font-display font-bold uppercase text-white leading-[0.9] tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-4">
              {heading}
            </h2>
          </motion.div>
        </div>
      </div>

      {/* Three pillars */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 sm:pt-20 pb-24 sm:pb-32">
        <div className="grid gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden sm:grid-cols-3">
          {pillars.map((p, i) => {
            const Icon = Icons[p.icon] || Icons.Target;
            return (
              <Reveal key={i} delay={i * 0.07} className="h-full">
                <div className="group h-full bg-[#0a0a0a] hover:bg-white/[0.04] transition-colors duration-500 p-7 sm:p-9" data-testid={`approach-pillar-${i}`}>
                  <div className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center mb-6 group-hover:border-white/40 transition-colors duration-500">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display uppercase tracking-wide text-white text-xl mb-3">{p.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
